import React, { useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Banner, Field, PasswordField } from "../../components/mui";
import { MdWarningAmber } from "react-icons/md";

/**
 * 登入（設計稿 el-pm-template_4.html:2166-2194 #screen-login）
 *
 * 版面就是一張置中的卡片：標題、兩個欄位、記住我、一顆滿版的登入鈕。
 * 原本那一大塊品牌圖示、英文標語與一整排寫死 px 的樣式常數全部拿掉 ——
 * 它們在大字模式不會跟著變大，而且登入頁要解決的只有一件事：讓人進得去。
 *
 * 整頁只有「登入」是主要按鈕（硬規則 1），忘記密碼是文字按鈕。
 * 帳號密碼打錯寫成紅色提示條，不是輸入框底下的灰色小字（硬規則 4）。
 *
 * 版權字樣與字級切換在 AuthLayout 的 footer，三個登入前的頁面共用。
 */
function App(props) {
  const navigate = useNavigate();
  // 「記住我」勾選過才會留下 LAST_ACCOUNT，用它回填帳號；
  // 勾選狀態本身依設計稿 L2181 的 checked 一律預設為勾選。
  const lastAccount = localStorage.getItem("LAST_ACCOUNT") || "";
  const [account, setAccount] = useState(lastAccount);
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (loading) return;
    if (!account.trim() || !password) {
      setError("請輸入帳號與密碼");
      return;
    }
    setError("");

    const callback = () => {
      // 設計稿的 login() 成功後跳出「歡迎回來，{姓名}」Toast。
      // Toast 由主版型（Layout）提供，登入頁不在其樹下，故用旗標交棒給登入後的第一個畫面。
      const name = localStorage.getItem("LAST_NAME") || "";
      sessionStorage.setItem("WELCOME_TOAST", name ? `歡迎回來，${name}` : "歡迎回來");
      // saga 成功時一律寫入 LAST_ACCOUNT／LAST_NAME，未勾記住我就在成功後清掉
      if (!remember) {
        localStorage.removeItem("LAST_ACCOUNT");
        localStorage.removeItem("LAST_NAME");
      }
      navigate("/");
    };

    props.POST_AuthAdminLogin(
      { Account: account.trim(), Password: password },
      callback,
      setLoading,
      (msg) => setError(msg || "帳號或密碼錯誤，請重新確認"),
    );
  };

  return (
    /*
      max-width 與上下留白照設計稿 L2167。width:100% 是這裡自己補的：
      AuthLayout 用 flex 置中，flex 子項預設是內容寬，不補的話卡片會隨著
      錯誤訊息的長短忽寬忽窄。
    */
    <Card component="form" onSubmit={handleSubmit} sx={{ width: "100%", maxWidth: 480, my: 6, mx: "auto" }}>
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography variant="h2" component="h1">
          登入 EL PM
        </Typography>

        <Stack spacing={2} sx={{ mt: 3 }}>
          <Field label="帳號" htmlFor="login-account">
            <TextField
              id="login-account"
              fullWidth
              value={account}
              autoComplete="username"
              placeholder="公司 Email"
              onChange={(e) => setAccount(e.target.value)}
            />
          </Field>

          <Field label="密碼" htmlFor="login-password">
            <PasswordField
              placeholder="請輸入密碼"
              id="login-password"
              fullWidth
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>

          {/* 進不去是這一頁最重要的後果，寫成橫幅而不是輸入框底下的灰字（硬規則 4） */}
          {error ? (
            <Banner kind="attention" icon={<MdWarningAmber />}>
              {error}
            </Banner>
          ) : null}

          {/* 勾選框放大、整列至少 44px 高：整行字都可以點（硬規則 3） */}
          <FormControlLabel
            sx={{ minHeight: "var(--hit-min)", mx: 0, gap: 1 }}
            control={
              <Checkbox
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                sx={{ p: 0.5, "& .MuiSvgIcon-root": { fontSize: 28 } }}
              />
            }
            label="在此裝置記住我（公用電腦請取消勾選）"
          />

          <Button variant="contained" fullWidth type="submit" aria-disabled={loading} loading={loading} loadingPosition="start">
            {loading ? "登入中…" : "登入"}
          </Button>

          <Button variant="text" onClick={() => navigate("/Auth/ForgotPassword")} sx={{ alignSelf: "flex-end" }}>
            忘記密碼？
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

const mapDispatchToProps = (dispatch) => ({
  POST_AuthAdminLogin(payload, callback, loading, onError) {
    dispatch({ type: "POST_AuthAdminLogin", payload, callback, loading, onError });
  },
});

App.propTypes = {
  POST_AuthAdminLogin: PropTypes.func,
};

App.defaultProps = {
  POST_AuthAdminLogin: () => {},
};

export default connect(null, mapDispatchToProps)(App);
